import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import * as api from '../api/client';
import { authLogout, saveLoginResponse } from '../api/client';
import { getCurrentEmail, getCurrentRole, canManagePricing } from '../lib/auth';
import { escapeHtml } from '../lib/cells';

interface SettingsData {
  loggingOut:   boolean;
  canManageMembers: boolean;           // owner·leader — 멤버 비번 초기화 관리
  members:      api.Member[] | null;   // null = 아직 로드 안 함/로딩 중
  membersError: string;
  resettingId:  string | null;
  resetDoneId:  string | null;
  // 비밀번호 변경
  pwCurrent:    string;
  pwNew:        string;
  pwConfirm:    string;
  pwError:      string;
  pwLoading:    boolean;
  pwSuccess:    boolean;
}

const ROLE_LABEL: Record<string, string> = {
  owner:   '최고관리자',
  leader:  '책임자',
  staff:   '직원',
  partner: '파트너',
  viewer:  '조회자',
};
function roleLabel(role: string): string {
  return ROLE_LABEL[role] ?? role;
}

/**
 * 설정.
 *
 * - 계정(로그아웃)
 * - 비밀번호 변경(본인)
 * - 멤버 관리(owner 전용) — 비밀번호 기본값 초기화
 * 향후: 표시 통화 / 다크 모드 / seed_conflict_log
 */
export class SettingsView extends SolaView<SettingsData> {
  override events = {
    click: {
      '@logout':   'handleLogout',
      '@reset':    'handleReset',
      '@pwsubmit': 'handlePwSubmit',
    },
    input:  { '@pwfield': 'handlePwField' },
    change: { '@pwfield': 'handlePwField' },
  };

  constructor() {
    super();
    this.data = {
      loggingOut:   false,
      canManageMembers: false,
      members:      null,
      membersError: '',
      resettingId:  null,
      resetDoneId:  null,
      pwCurrent:    '',
      pwNew:        '',
      pwConfirm:    '',
      pwError:      '',
      pwLoading:    false,
      pwSuccess:    false,
    };
  }

  override getTitle(): string {
    return '설정 — HCLTech 어드민';
  }

  override onMount(): void {
    // 서버가 진짜 게이트(requireRole('owner','leader')). 이건 UI 노출용.
    const role = getCurrentRole();
    this.data.canManageMembers = role === 'owner' || role === 'leader';
    if (this.data.canManageMembers) void this.loadMembers();
  }

  // ─── 데이터 로딩 ─────────────────────────────────────────────────
  private async loadMembers(): Promise<void> {
    try {
      this.data.members = await api.fetchMembers();
      this.data.membersError = '';
    } catch (e) {
      this.data.members = [];
      this.data.membersError = `멤버 목록 로드 실패: ${(e as Error).message}`;
    }
    this.rerenderAll();
  }

  /**
   * 본문 재렌더.
   *
   * ★ `this.render()` 를 다시 부르면 안 된다 (2026-08-18).
   *   `htmlSolaWithInstance(this)` 는 내부에서
   *   `createElementFromHtml(..., this)` → `this.attach(새 element)` 를 부르고,
   *   `attach()` 는 `_rootElement` 를 **방금 만든(문서에 붙지 않은) element** 로
   *   갈아치운다. 그래서 예전 코드
   *
   *     this._rootElement.innerHTML = this.render().innerHTML;
   *
   *   는 **첫 호출만** 동작했다. 두 번째부터는 `_rootElement` 가 고아라
   *   화면이 바뀌지 않는다 — 멤버 목록 로드(1회차)는 되는데 그 뒤 '비번 초기화'
   *   가 서버에서는 성공하고도 배지가 안 뜨던 이유다.
   *
   *   코어가 이 함정 때문에 `refreshRegion` 을 제공한다. render() 를 부르지
   *   않아 `_rootElement` 가 마운트된 노드로 유지되고, 교체된 subtree 의
   *   [data-bind] 재등록도 해 준다. 이벤트는 root 위임이라 영향 없다.
   */
  private rerenderAll(): void {
    this.refreshRegion('.settings-body', () => this.renderBody());
  }

  // ─── 로그아웃 ────────────────────────────────────────────────────
  async handleLogout(_e: Event, target: HTMLElement): Promise<void> {
    if (this.data.loggingOut) return;
    this.data.loggingOut = true;
    const btn = target as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = '로그아웃 중…';
    try {
      await authLogout();
    } finally {
      location.replace('/login');
    }
  }

  // ─── 비밀번호 변경 (본인) ────────────────────────────────────────
  handlePwField(_e: Event, target: HTMLElement): void {
    const name = target.getAttribute('data-name');
    const val = (target as HTMLInputElement).value;
    if      (name === 'pwCurrent') this.data.pwCurrent = val;
    else if (name === 'pwNew')     this.data.pwNew     = val;
    else if (name === 'pwConfirm') this.data.pwConfirm = val;
    else return;
    this.clearPwFeedback();   // 입력 시 재렌더 없이 에러/성공 메시지만 정리(커서 보호)
  }

  private validatePw(): string | null {
    const n = this.data.pwNew, c = this.data.pwConfirm;
    if (n.length < 8)       return '비밀번호는 8자 이상이어야 합니다';
    if (n.length > 72)      return '비밀번호는 72자 이하여야 합니다';
    if (!/[A-Za-z]/.test(n)) return '비밀번호에 영문이 포함되어야 합니다';
    if (!/[0-9]/.test(n))    return '비밀번호에 숫자가 포함되어야 합니다';
    if (n === 'hcltech1234') return '기본 비밀번호와 다른 값을 사용하세요';
    if (n !== c)            return '새 비밀번호 확인이 일치하지 않습니다';
    return null;
  }

  async handlePwSubmit(): Promise<void> {
    if (this.data.pwLoading) return;
    if (!this.data.pwCurrent) { this.setPwError('현재 비밀번호를 입력하세요'); return; }
    const err = this.validatePw();
    if (err) { this.setPwError(err); return; }

    this.setPwError('');
    this.setPwLoading(true);
    try {
      const res = await api.authChangePassword(this.data.pwCurrent, this.data.pwNew);
      saveLoginResponse(res, getCurrentEmail() ?? '');
      // 성공 — 필드 비우고 성공 메시지(재렌더).
      this.data.pwCurrent = '';
      this.data.pwNew     = '';
      this.data.pwConfirm = '';
      this.data.pwLoading = false;
      this.data.pwError   = '';
      this.data.pwSuccess = true;
      this.rerenderAll();
    } catch (e) {
      this.setPwLoading(false);
      this.setPwError(e instanceof Error ? e.message : '비밀번호 변경 실패');
    }
  }

  // 부분 갱신 헬퍼 — 입력 중 재렌더 회피(커서 보호).
  private setPwError(msg: string): void {
    this.data.pwError = msg;
    const el = this._rootElement?.querySelector<HTMLElement>('.settings-pw-error');
    if (el) { el.textContent = msg; el.classList.toggle('hidden', !msg); }
  }
  private setPwLoading(on: boolean): void {
    this.data.pwLoading = on;
    const root = this._rootElement;
    if (!root) return;
    const btn = root.querySelector<HTMLButtonElement>('.settings-pw-submit');
    if (btn) { btn.disabled = on; btn.textContent = on ? '변경 중…' : '비밀번호 변경'; }
    root.querySelectorAll<HTMLInputElement>('input[data-action="pwfield"]').forEach((i) => { i.disabled = on; });
  }
  private clearPwFeedback(): void {
    if (this.data.pwError) {
      this.data.pwError = '';
      this._rootElement?.querySelector('.settings-pw-error')?.classList.add('hidden');
    }
    if (this.data.pwSuccess) {
      this.data.pwSuccess = false;
      this._rootElement?.querySelector('.settings-pw-success')?.classList.add('hidden');
    }
  }

  // ─── 비밀번호 초기화 (owner) ─────────────────────────────────────
  async handleReset(_e: Event, target: HTMLElement): Promise<void> {
    if (this.data.resettingId) return;
    const id    = target.getAttribute('data-id');
    const email = target.getAttribute('data-email') ?? '';
    if (!id) return;
    const ok = window.confirm(
      `${email} 님의 비밀번호를 기본값(hcltech1234)으로 초기화합니다.\n` +
      `해당 멤버는 다음 로그인 시 새 비밀번호를 설정해야 합니다. 계속할까요?`,
    );
    if (!ok) return;

    this.data.resettingId = id;
    this.data.resetDoneId = null;
    this.data.membersError = '';
    this.rerenderAll();
    try {
      await api.resetMemberPassword(id);
      const m = this.data.members?.find((x) => x.id === id);
      if (m) m.must_change_password = true;
      this.data.resetDoneId = id;
    } catch (e) {
      this.data.membersError = `초기화 실패: ${(e as Error).message}`;
    } finally {
      this.data.resettingId = null;
      this.rerenderAll();
    }
  }

  // ─── 렌더 ────────────────────────────────────────────────────────
  private renderPwChange(): string {
    const d = this.data;
    return `
      <div class="settings-card">
        <h2>비밀번호 변경</h2>
        <div class="settings-form">
          <label class="settings-field">
            <span>현재 비밀번호</span>
            <input class="input" type="password" autocomplete="current-password"
                   data-action="pwfield" data-name="pwCurrent" value="${escapeHtml(d.pwCurrent)}">
          </label>
          <label class="settings-field">
            <span>새 비밀번호</span>
            <input class="input" type="password" autocomplete="new-password"
                   data-action="pwfield" data-name="pwNew" value="${escapeHtml(d.pwNew)}">
            <small class="settings-hint">8자 이상, 영문 + 숫자 포함, 기본 비밀번호 금지</small>
          </label>
          <label class="settings-field">
            <span>새 비밀번호 확인</span>
            <input class="input" type="password" autocomplete="new-password"
                   data-action="pwfield" data-name="pwConfirm" value="${escapeHtml(d.pwConfirm)}">
          </label>
          <div class="settings-pw-error text-danger ${d.pwError ? '' : 'hidden'}">${escapeHtml(d.pwError)}</div>
          <div class="settings-pw-success text-success ${d.pwSuccess ? '' : 'hidden'}">비밀번호가 변경되었습니다.</div>
          <button class="btn btn-primary settings-pw-submit" type="button"
                  data-action="pwsubmit" ${d.pwLoading ? 'disabled' : ''}>
            ${d.pwLoading ? '변경 중…' : '비밀번호 변경'}
          </button>
        </div>
      </div>`;
  }

  private renderMembers(): string {
    if (!this.data.canManageMembers) return '';
    const rows = this.data.members;
    let inner: string;
    if (rows === null) {
      inner = `<p class="text-muted">불러오는 중…</p>`;
    } else if (this.data.membersError && rows.length === 0) {
      inner = `<p class="text-danger">${escapeHtml(this.data.membersError)}</p>`;
    } else if (rows.length === 0) {
      inner = `<p class="text-muted">멤버가 없습니다.</p>`;
    } else {
      const myRole = getCurrentRole();
      const body = rows.map((m) => {
        const busy = this.data.resettingId === m.id;
        const done = this.data.resetDoneId === m.id;
        // 권한상승 방지: leader 는 owner 계정을 초기화할 수 없음(서버도 403). 버튼 비활성.
        const blocked = myRole !== 'owner' && m.role === 'owner';
        return `
          <tr>
            <td>${escapeHtml(m.name || '-')}</td>
            <td class="text-muted">${escapeHtml(m.email)}</td>
            <td>${escapeHtml(roleLabel(m.role))}</td>
            <td class="text-right">
              ${done ? `<span class="settings-reset-done">초기화됨 · 변경 대기</span> ` : ''}
              <button class="btn btn-secondary btn-sm settings-reset-btn" type="button"
                      data-action="reset" data-id="${escapeHtml(m.id)}"
                      data-email="${escapeHtml(m.email)}" ${busy || blocked ? 'disabled' : ''}
                      ${blocked ? 'title="owner 계정은 owner만 초기화할 수 있습니다"' : ''}>
                ${busy ? '초기화 중…' : '비번 초기화'}
              </button>
            </td>
          </tr>`;
      }).join('');
      inner = `
        <table class="settings-members-table">
          <thead>
            <tr><th>이름</th><th>이메일</th><th>역할</th><th class="text-right">작업</th></tr>
          </thead>
          <tbody>${body}</tbody>
        </table>`;
    }
    return `
      <div class="settings-card">
        <h2>멤버 관리</h2>
        <p class="settings-card-desc">비밀번호를 기본값(<code>hcltech1234</code>)으로 초기화합니다.
           해당 멤버는 다음 로그인 시 새 비밀번호를 설정해야 합니다.</p>
        ${this.data.membersError && (rows?.length ?? 0) > 0
          ? `<p class="text-danger">${escapeHtml(this.data.membersError)}</p>` : ''}
        ${inner}
      </div>`;
  }

  /**
   * 본문만 문자열로. 갱신은 이 결과만 `.settings-body` 에 갈아 끼운다
   * (위 `rerenderAll` 주석 참조). 껍데기(header)는 한 번 그리고 건드리지 않는다.
   */
  private renderBody(): string {
    const email = getCurrentEmail();
    const busy = this.data.loggingOut;
    return `
          <div class="settings-card">
            <h2>계정</h2>
            ${email ? `<p class="settings-account-email">${escapeHtml(email)}</p>` : ''}
            <button class="btn btn-secondary settings-logout-btn" type="button"
                    data-action="logout" ${busy ? 'disabled' : ''}>
              ${busy ? '로그아웃 중…' : '로그아웃'}
            </button>
          </div>

          ${this.renderPwChange()}

          ${this.renderMembers()}

          ${canManagePricing() ? `
          <div class="settings-card">
            <h2>임대 견적</h2>
            <p class="settings-card-desc">상품군별 임대 계산식(감가율·금융비용·마진율·단기 할증·위약금)을
               관리합니다. 여기 값이 곧 계산식이라 코드 배포 없이 바뀝니다.</p>
            <a class="btn btn-secondary" data-link href="/settings/rental-preset">임대 계산식 프리셋 열기</a>
          </div>` : ''}

          <div class="placeholder-card">
            <h2>향후 구현 예정</h2>
            <ul>
              <li>표시 통화 토글 (USD / KRW / 원본)</li>
              <li>다크 모드</li>
              <li>seed_conflict_log 관리 (관리자 권한)</li>
            </ul>
          </div>`;
  }

  /** ★ 프레임워크(마운트)만 부른다. 앱 코드에서 직접 부르지 말 것. */
  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <div class="page page-settings">
        <header class="page-header">
          <h1>⚙️ 설정</h1>
        </header>
        <section class="page-body settings-body">${this.renderBody()}</section>
      </div>
    `;
  }
}
